import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  ChangeEvent,
} from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Layout, Button, Input, Select } from '../../../components';
import SelectType from '../../../components/Form/Select/select_type';
import { useToast } from '../../../hooks/toast';
import { RoutesPaths } from '../../../routes';
import api from '../../../services/api';
import type { InstructionDataDTO } from '../../../shared/interfaces/instructions';
import type { ParamsProps } from '../../../shared/interfaces/parameter';
import type { ScreenData } from '../../../shared/interfaces/screen';
import getValidationErrors from '../../../utils/getValidationErrors';
import { Container, FormContainer, FormFooter } from './styles';

const Editar: React.FC = () => {
  const formRef = useRef<FormHandles | null>(null);
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<ParamsProps>();
  const { addToast } = useToast();
  const [isLoadingInput, setIsLoadingInput] = useState<boolean>(true);
  const [dataTable, setDataTable] = useState<SelectType[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<InstructionDataDTO>({
    id: '',
    nomeDaInstrucao: '',
    textoDaInstrucao: '',
    listaDeTelas: [],
  });
  let selectedScreens = useRef<number[] | null>(null);

  useEffect(() => {
    if (id !== undefined) {
      findInstruction(id);
    }
  }, [id]);

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get<ScreenData[]>(`/tela`);
      setIsLoadingInput(false);
      let screenValues: SelectType[] = response.data.map(
        (screen: ScreenData) => {
          return {
            value: screen.id,
            label: screen.titulo,
          };
        },
      );

      setDataTable(screenValues);
    } catch (e) {
      console.error(e);
    }
  }, []);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function updateValue(e: ChangeEvent<HTMLInputElement>) {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  }

  const handleSubmit = useCallback(
    async (instruction: InstructionDataDTO) => {
      setIsLoading(true);
      instruction.listaDeTelas = selectedScreens.current;

      try {
        if (!instruction.listaDeTelas?.length) {
          const schemaSelect = Yup.object().shape({
            id: Yup.string().max(25, 'No máximo 25 caracteres'),
            nomeDaInstrucao: Yup.string()
              .max(40, 'No máximo 40 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
            textoDaInstrucao: Yup.string()
              .max(200, 'No máximo 200 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
            select: Yup.string().required('Este campo é obrigatório!'),
          });
          await schemaSelect.validate(instruction, {
            abortEarly: false,
          });
        }
        const schema = Yup.object().shape({
          id: Yup.string().max(25, 'No máximo 25 caracteres'),
          nomeDaInstrucao: Yup.string()
            .max(40, 'No máximo 40 caracteres')
            .matches(
              /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
              'Um ou mais caracteres digitados não são permitidos!',
            )
            .required('Este campo é obrigatório!'),
          textoDaInstrucao: Yup.string()
            .max(200, 'No máximo 200 caracteres')
            .matches(
              /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
              'Um ou mais caracteres digitados não são permitidos!',
            )
            .required('Este campo é obrigatório!'),
        });

        await schema.validate(instruction, {
          abortEarly: false,
        });

        if (id !== undefined) {
          await api.put(`/instrucao/${id}`, instruction);
          setIsLoading(false);

          addToast({
            type: 'success',
            title: 'Instrução alterada com sucesso!',
            description: 'As informações da Instrução foram alteradas.',
          });
        } else {
          await api.post('/instrucao', instruction);
        }

        setTimeout(() => {
          history.push(RoutesPaths.listInstruction);
        }, 4000);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errorMessages = getValidationErrors(err);
          formRef.current?.setErrors(errorMessages);
          return;
        } else {
          console.error(err);
          addToast({
            type: 'error',
            title: 'Instrução já cadastrada!',
            description:
              'As informações que foram inseridas, já estão presentes no sistema.',
          });
        }
      }
    },
    [addToast, history, id],
  );

  async function findInstruction(id: string) {
    const response = await api.get<InstructionDataDTO>(`/instrucao/${id}`);
    setData({
      id: id,
      nomeDaInstrucao: response.data.nomeDaInstrucao,
      textoDaInstrucao: response.data.textoDaInstrucao,
      listaDeTelas: response.data.listaDeTelas,
    });

    selectedScreens.current = response.data.listaDeTelas;
  }

  function back() {
    if (location.pathname.match('/listar')) {
      history.push(RoutesPaths.home);
    } else {
      history.goBack();
    }
  }

  function handleScreenChange(dataTable: [] | null) {
    let screens: number[] = [];
    if (dataTable) {
      screens = dataTable.map((item: any) => item['value']);
    }

    const newData = data;
    newData.listaDeTelas = screens;

    selectedScreens.current = screens;
    setData(newData);
  }

  return (
    <>
      <Layout title={'Editar Instrução'} hideFloatingButton>
        <Container>
          <FormContainer>
            <Form onSubmit={handleSubmit} ref={formRef}>
              <Input
                type="text"
                label="Nome"
                name="nomeDaInstrucao"
                value={data.nomeDaInstrucao}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateValue(e)}
                loading={isLoadingInput}
              ></Input>

              <Input
                type="text"
                label="Texto"
                name="textoDaInstrucao"
                value={data.textoDaInstrucao}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateValue(e)}
                loading={isLoadingInput}
              ></Input>

              <Select
                name={'select'}
                options={dataTable}
                onChange={handleScreenChange}
                placeholder="Selecione"
                initialValues={data.listaDeTelas}
                label="Telas"
                loading={isLoadingInput}
              />

              <FormFooter>
                <Button
                  variant="solid"
                  style={{ marginTop: '1rem' }}
                  type="submit"
                >
                  Salvar
                </Button>
                <Button
                  variant="outline"
                  color={'#C71F37'}
                  style={{ marginTop: '1rem' }}
                  onClick={() =>
                    setTimeout(() => {
                      back();
                    }, 300)
                  }
                >
                  Cancelar
                </Button>
              </FormFooter>
            </Form>
          </FormContainer>
        </Container>
      </Layout>
    </>
  );
};

export default Editar;
